import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ConfirmationService, PaginatorModule } from 'primeng';
import { VmeCommonModule } from '../../vme-common.module';
import { VmePipesModule } from '../../vme-pipes.module';
import { UserService } from '../../services';
import { Message } from '../../classes/Message';
import { OrderBookingHealthCheckService } from '../../services/order-booking-health-check.service';
import { OrderBookingTabsComponent } from './order-booking-tabs/order-booking-tabs.component';
import { OrderBookingFiltersComponent } from './order-booking-filters/order-booking-filters.component';
import { BookingListItemComponent } from './booking-list-item/booking-list-item.component';
import { SelectedBookingComponent } from './selected-booking/selected-booking.component';
import { OrderBookingCheckInfoComponent } from './order-booking-check-info/order-booking-check-info.component';
import {
  IAssigneeChange,
  IOrderBookingSummary,
  IOrderBookingHealthCheckDTO,
} from '../../interfaces/order-booking-health-check/order-booking-health-check';
import {
  IOrderBookingSearchFilters,
  IOrderBookingSearchFilterValues,
} from '../../interfaces/order-booking-health-check/order-booking-search';
import { OrderBookingHealthCheckTab } from '../../enums/order-booking-health-check/order-booking-health-check.enums';

@Component({
  selector: 'order-booking-health-check',
  standalone: true,
  imports: [
    CommonModule,
    VmeCommonModule,
    VmePipesModule,
    PaginatorModule,
    OrderBookingTabsComponent,
    OrderBookingFiltersComponent,
    BookingListItemComponent,
    SelectedBookingComponent,
    OrderBookingCheckInfoComponent,
  ],
  templateUrl: './order-booking-health-check.component.html',
  styleUrls: ['./order-booking-health-check.component.scss'],
})
export class OrderBookingHealthCheckComponent implements OnInit {
  protected data: IOrderBookingHealthCheckDTO;
  protected paginatedBookings: IOrderBookingSummary[] = [];
  protected selectedBookingIndex: number = 0;

  protected selectedTab: OrderBookingHealthCheckTab = OrderBookingHealthCheckTab.AllBookings;
  protected tabCounts: Record<OrderBookingHealthCheckTab, number>;

  protected pageSize: number = 10;
  protected pageIndex: number = 0;

  protected isLoading: boolean = true;
  protected isUserOrderBookingHealthCheckAdmin: boolean = false;
  protected currentDate = Date.now();

  // The filters as they were when Search was pressed, so a refresh re-applies those.
  private lastFilterValues: IOrderBookingSearchFilterValues = null;

  protected rechecking = false;

  private currentUserId: string;

  constructor(
    private orderBookingHealthCheckService: OrderBookingHealthCheckService,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private confirmationService: ConfirmationService,
  ) {
    this.currentUserId = this.userService.currentUser.id;

    this.route.queryParams.subscribe((params: Params) => {
      if (params['selectedTab'] != null) {
        this.selectedTab = Number(params['selectedTab']);
      }
    });
  }

  async ngOnInit() {
    await this.refreshAll();

    this.userService.isUserInRole('OrderBookingHealthCheckAdmin').then((isAdmin) => {
      this.isUserOrderBookingHealthCheckAdmin = isAdmin;
    });
  }

  protected async refreshAll() {
    await this.loadData(this.lastFilterValues);
  }

  protected async handleSearch(filterValues: IOrderBookingSearchFilterValues) {
    await this.loadData(filterValues);
  }

  // Counts arrive with the search results, so the badges always describe the same filtered set
  // as the list.
  private applyTabCounts() {
    const counts = this.data?.counts;

    this.tabCounts = {
      [OrderBookingHealthCheckTab.AllBookings]: counts?.allBookings ?? 0,
      [OrderBookingHealthCheckTab.AssignedToYou]: counts?.assignedToYou ?? 0,
      [OrderBookingHealthCheckTab.AssignedToOthers]: counts?.assignedToOthers ?? 0,
      [OrderBookingHealthCheckTab.Unassigned]: counts?.unassigned ?? 0,
    };
  }

  async loadData(filterValues: IOrderBookingSearchFilterValues = null) {
    this.isLoading = true;
    this.lastFilterValues = filterValues;

    const filters: IOrderBookingSearchFilters = {
      selectedTab: { value: this.selectedTab },
      customerIds: { value: filterValues?.customerIds ?? [] },
      departmentIds: { value: filterValues?.departmentIds ?? [] },
      teamIds: { value: filterValues?.teamIds ?? [] },
      checkTypes: { value: filterValues?.checkTypes ?? [] },
      issueProgress: { value: filterValues?.issueProgress ?? [] },
      priorities: { value: filterValues?.priorities ?? [] },
      bookingStatuses: { value: filterValues?.bookingStatuses ?? [] },
      raisedWithinDays: { value: filterValues?.raisedWithinDays ?? 1 },
      supplierAssigned: { value: filterValues?.supplierAssigned ?? null },
      searchText: { value: filterValues?.searchText ?? '' },
    };

    try {
      this.data = await this.orderBookingHealthCheckService.get({ filters });
    } catch {
      this.data = null;
      Message.Error('Could not load the bookings. Please try again.');
    } finally {
      this.isLoading = false;
    }

    this.applyTabCounts();
    this.pageIndex = 0;
    this.selectedBookingIndex = 0;
    this.paginateData();
  }

  async handleTabChanged(tab: OrderBookingHealthCheckTab) {
    await this.updateQueryParam('selectedTab', tab.toString());
    this.selectedTab = tab;
    await this.loadData(this.lastFilterValues);
  }

  async updateQueryParam(paramName: string, paramValue: string) {
    const queryParams = { ...this.route.snapshot.queryParams };
    queryParams[paramName] = paramValue;

    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  protected selectBooking(orderId: number) {
    const index = this.paginatedBookings.findIndex((x) => x.orderId === orderId);
    this.selectedBookingIndex = index === -1 ? 0 : index;
  }

  // Never on opening a booking, which raced the scheduled pass. Patched into this row rather than
  // reloaded, or a booking whose last issue just cleared would vanish under the operator.
  protected async recheckSelected(orderId: number) {
    if (!orderId) {
      return;
    }

    this.rechecking = true;

    try {
      const refreshed = await this.orderBookingHealthCheckService.reevaluate(orderId);
      if (refreshed) {
        this.patchBooking(refreshed);
      }
    } catch {
      // A failed re-check just means the page keeps showing the last scheduled result, which is
      // no worse than before. Not worth interrupting the operator over.
    } finally {
      this.rechecking = false;
    }
  }

  // Replaces one booking in both the full result set and the current page, immutably, so change
  // detection picks it up without disturbing the rest of the list.
  private patchBooking(booking: IOrderBookingSummary) {
    const replace = (list: IOrderBookingSummary[]) => {
      const index = list.findIndex((x) => x.orderId === booking.orderId);
      if (index === -1) {
        return list;
      }
      const next = [...list];
      next[index] = booking;
      return next;
    };

    this.paginatedBookings = replace(this.paginatedBookings);

    if (this.data?.bookings) {
      this.data = { ...this.data, bookings: replace(this.data.bookings) };
    }
  }

  protected paginate($event: any) {
    this.pageSize = $event.rows;
    this.pageIndex = $event.page;
    this.paginateData();
  }

  protected paginateData() {
    this.paginatedBookings = (this.data?.bookings ?? []).slice(
      this.pageIndex * this.pageSize,
      (this.pageIndex + 1) * this.pageSize,
    );

    // Paging onto a shorter final page can leave the selection past the end, which blanks the
    // Selected Booking panel.
    if (this.selectedBookingIndex >= this.paginatedBookings.length) {
      this.selectedBookingIndex = 0;
    }
  }

  protected trackByOrderId(index: number, booking: IOrderBookingSummary): number {
    return booking.orderId;
  }

  // Re-check the booking that changed rather than reloading the queue, so a booking whose last
  // issue was just closed stays put until the operator navigates away.
  protected async handleIssueChanged() {
    await this.recheckSelected(this.paginatedBookings[this.selectedBookingIndex]?.orderId);
  }

  // Moved by the one change rather than recounted: data.bookings is narrowed to the current tab,
  // so it cannot supply the other tabs' totals. All Bookings is untouched either way.
  protected handleAssigneeChanged(change: IAssigneeChange) {
    const from = this.assigneeTab(change.previousAssigneeId);
    const to = this.assigneeTab(change.assigneeId);

    if (from === to) {
      return;
    }

    this.tabCounts = {
      ...this.tabCounts,
      [from]: Math.max(0, this.tabCounts[from] - 1),
      [to]: this.tabCounts[to] + 1,
    };
  }

  private assigneeTab(assigneeId: string): OrderBookingHealthCheckTab {
    if (assigneeId == null) {
      return OrderBookingHealthCheckTab.Unassigned;
    }

    return assigneeId === this.currentUserId
      ? OrderBookingHealthCheckTab.AssignedToYou
      : OrderBookingHealthCheckTab.AssignedToOthers;
  }

  protected unassignMe() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to unassign yourself from all bookings?',
      accept: async () => {
        await this.orderBookingHealthCheckService.unassignMe();
        await this.refreshAll();
      },
    });
  }

  protected unassignAll() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to unassign every user from every booking?',
      accept: async () => {
        await this.orderBookingHealthCheckService.unassignAll();
        await this.refreshAll();
      },
    });
  }
}
